/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import PropTypes from 'util/PropTypes';
import classnames from 'classnames';
import { SVGIcon } from 'ca-ui-toolkit';
import chevronDown16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-down_16';
import chevronUp16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-up_16';

import _ from 'underscore';

import './caretToolbarItem.scss';

class CaretToolbarItem extends Component {

	constructor(props) {
		super(props);

		this.state = {
			open: false
		};
	}

	render() {
		if (this.props.hidden) {
			return null;
		}

		const forwardedProps = _.omit(this.props, 'children', 'onPrimaryAction', 'enabled', 'selected', 'hidden', 'icon', 'iconId', 'label');

		const classes = classnames({
			'explore-vis-toolbar__caret-item': true,
			'explore-vis-toolbar__caret-item--selected': this.props.selected,
			'explore-vis-toolbar__caret-item--enabled': this.isEnabled(),
			'explore-vis-toolbar__caret-item--actionable': this.isActionable(),
			'explore-vis-toolbar__caret-item--has-text': !!(this.props.label)
		});

		const icon = this.renderIcon(this.props.icon) || this.renderIconId(this.props.iconId);
		const label = (this.props.size !== 'small' || !icon) ? this.renderLabel(this.props.label) : undefined;
		const caret = this.renderCaret(this.props.selected);
		const tabIndex = this.isActionable() ? 0 : -1;
		/* use ariaLabel in props if it is defined. Otherwise use title
+		when both of them are falsy, react is smart enough not to render an undefined attribute */
		const ariaLabel = this.props.ariaLabel || this.props.title;
		const id = this.props.id;

		return (
			<div
				className={classes}
				onMouseDown={(...params) => this.performAction(...params)}
				onTouchStart={(...params) => this.performAction(...params)}
				// ↓ on mobile to suppress the default behavior of a tap - eliminate the superfluous mousedown event
				onTouchEnd={event => event.preventDefault()}
				onKeyPress={(...params) => this.onKeyPress(...params)}
				tabIndex={tabIndex}
				aria-label={ariaLabel}
				data-id={id}
				appcues-data-id={id}
				{...forwardedProps}
			>
				{icon}
				{label}
				{this.props.children}
				{caret}
			</div>
		);
	}

	renderIcon(icon) {
		if (!icon) {
			return;
		}

		return (
			<div className="explore-vis-toolbar__caret-item__icon">
				{icon}
			</div>
		);
	}

	renderIconId(id) {
		if (!id) {
			return;
		}

		return this.renderIcon(
			<SVGIcon iconId={id} />
		);
	}

	renderLabel(text) {
		if (!text) {
			return;
		}

		return (
			<div className="explore-vis-toolbar__caret-item__text">
				{text}
			</div>
		);
	}

	renderCaret(selected) {
		let id;
		if (this.props.arrow === 'up') {
			id = selected ? chevronDown16.id : chevronUp16.id;
		} else {
			id = selected ? chevronUp16.id : chevronDown16.id;
		}

		return (
			<div className="explore-vis-toolbar__caret-item__caret">
				<SVGIcon iconId={id} size="small" />
			</div>
		);
	}

	isEnabled() {
		return this.props.enabled;
	}

	isActionable() {
		return this.isEnabled() && this.props.onPrimaryAction;
	}

	onEnter(event) {
		this.performAction(event);
	}

	onKeyPress(event) {
		const handler = {
			'Enter': () => this.onEnter(event)
		}[event.key];

		handler && handler();
	}

	performAction(event) {
		if (this.isActionable()) {
			this.props.onPrimaryAction(event);
		}
	}

}

CaretToolbarItem.propTypes = {
	children: PropTypes.any,
	id: PropTypes.string,
	icon: PropTypes.node,
	iconId: PropTypes.string,
	arrow: PropTypes.string,
	ariaLabel: PropTypes.string,
	label: PropTypes.string,
	title: PropTypes.string,
	hidden: PropTypes.bool,
	enabled: PropTypes.bool,
	selected: PropTypes.bool,
	onPrimaryAction: PropTypes.func,
	size: PropTypes.string
};

CaretToolbarItem.defaultProps = {
	children: [],
	arrow: 'down',
	hidden: false,
	enabled: true,
	selected: false
};

export default CaretToolbarItem;



// WEBPACK FOOTER //
// ./src/js/components/toolbar/items/CaretToolbarItem.js